import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getWorkspaceId,
  useAppSelector,
  useLazyGetCustomerDetailsQuery,
  useGetFieldOpsCustomerProfileDetailsMutation,
  useLazyGetConversationsQuery,
  dispatch,
  updateQueryThread,
  ConversationData,
  getIsFieldOps,
} from '@zonofi/zono-money-store';
import { ReactComponent as PhoneIcon } from '@zonofi/svg-icons/phone-icon.svg';
import { ReactComponent as FirmIcon } from '@zonofi/svg-icons/firm-icon.svg';
import { ReactComponent as UserIcon } from '@zonofi/svg-icons/user-icon.svg';
import { ReactComponent as CodeIcon } from '@zonofi/svg-icons/code-icon.svg';
import { ReactComponent as EnvelopeIcon } from '@zonofi/svg-icons/envelope.svg';
import { isNull } from 'lodash';
import { SVGS } from '../../assets';
import { FIELD_OPS_ROUTES, SELLER_WEBAPP_ROUTES } from '../../constants';

export const useCustomerDetails = () => {
  const navigate = useNavigate();
  const { customerId } = useParams();
  const [include, setInclude] = useState(false);
  const [getCustomerDetailsApi, customerDetailsApi] =
    useLazyGetCustomerDetailsQuery();
  const [getCustomerProfileSummaryApi, customerProfileSummaryApi] =
    useGetFieldOpsCustomerProfileDetailsMutation();
  const [getConversationsApi, conversationsApi] =
    useLazyGetConversationsQuery();

  const isFieldOps = useAppSelector(getIsFieldOps);
  const sellerWorkspaceId = useAppSelector(getWorkspaceId);

  useEffect(() => {
    if (customerId) {
      getCustomerDetailsApi({
        sellerWorkspaceId,
        inviteId: customerId,
        includeInactive: true,
      });
    }
  }, [customerId]);

  useEffect(() => {
    if (!customerId || !sellerWorkspaceId) return;

    getCustomerProfileSummaryApi({
      sellerWorkspaceId,
      customerId,
      params: {
        inviteIds: [customerId],
      },
    });
    getConversationsApi({
      sellerWorkspaceId,
      limit: 1,
      customerId,
      channelTypes: ['C', 'CUST'],
    });
  }, [customerId, sellerWorkspaceId]);

  const customerData = customerDetailsApi.data;

  useEffect(() => {
    if (!customerData) return;

    if (isNull(customerData?.excludedAndBlockedBotAudience)) {
      setInclude(true);
    } else {
      setInclude(false);
    }
  }, [customerData?.excludedAndBlockedBotAudience]);

  const customerInfo = useMemo(() => {
    if (!customerData) return [];
    const formattedPhone = customerData?.telephone
      ? `+${customerData.telephone.trim()}`
      : 'N/A';

    return [
      {
        id: 0,
        icon: <FirmIcon color="#000" />,
        title: 'Firm Name',
        value: customerData?.companyName || 'N/A',
      },
      {
        id: 1,
        icon: <UserIcon color="#000" />,
        title: 'Customer Name',
        value: customerData?.name || 'N/A',
      },
      {
        id: 2,
        icon: <CodeIcon color="#000" />,
        title: 'Customer Code',
        value: customerData?.distributorCode || 'N/A',
      },
      // {
      //   id: 3,
      //   icon: SVGS.Location,
      //   title: 'Address',
      //   value: address1 || address2 ? `${address1}, ${address2}`.trim() : 'N/A',
      // },
      {
        id: 3,
        icon: <EnvelopeIcon color="#000" />,
        title: 'Email ID',
        value: customerData?.email || 'N/A',
      },
      {
        id: 4,
        icon: <PhoneIcon color="#000" />,
        title: 'Phone Number',
        value: formattedPhone,
      },
    ];
  }, [customerData]);

  const formattedMobile = useMemo(() => {
    if (!customerData?.telephone) return 'N/A';
    return `+${customerData.telephone.trim()}`;
  }, [customerData?.telephone]);

  const customerProfileSummary = customerProfileSummaryApi?.data?.data?.[0];

  const formatAmount = (amount: number | string | null | undefined) => {
    if (amount === null || amount === undefined || amount === '') return '₹0';

    const numeric = Number((Number(amount) / 100).toFixed(2));
    if (Number.isNaN(numeric)) return '₹0';
    return `₹${numeric.toLocaleString('en-IN')}`;
  };

  const formatDate = (date: string | null | undefined) => {
    if (!date) return '-';
    const parsedDate = new Date(date);
    if (Number.isNaN(parsedDate.getTime())) return '-';
    return parsedDate.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const activities = useMemo(() => {
    if (!customerProfileSummary) return [];

    const items = [
      customerProfileSummary?.latestInvoice && {
        id: customerProfileSummary.latestInvoice.invoiceId ?? 'latest-invoice',
        type: 'invoice' as const,
        title: 'Invoice Generated',
        subtitle: customerProfileSummary.latestInvoice.invoiceNumber ?? '-',
        date: formatDate(customerProfileSummary.latestInvoice.invoiceDate),
        amount: formatAmount(customerProfileSummary.latestInvoice.totalValue),
      },
      customerProfileSummary?.latestOrder && {
        id: customerProfileSummary.latestOrder.pofileid ?? 'latest-order',
        type: 'order' as const,
        title: 'Order Placed',
        subtitle: customerProfileSummary.latestOrder.refOrderNumber ?? '-',
        date: formatDate(customerProfileSummary.latestOrder.orderDate),
        amount: formatAmount(customerProfileSummary.latestOrder.totalValue),
      },
      customerProfileSummary?.latestPayment && {
        id: customerProfileSummary.latestPayment.paymentId ?? 'latest-payment',
        type: 'payment' as const,
        title: 'Payment Received',
        subtitle: customerProfileSummary.latestPayment.paymentCode ?? '-',
        date: formatDate(customerProfileSummary.latestPayment.paymentDate),
        amount: formatAmount(customerProfileSummary.latestPayment.totalValue),
      },
    ].filter(Boolean);

    return items;
  }, [customerProfileSummary]);

  const normalizeConversationStatus = (
    status: string | undefined
  ): 'open' | 'in-progress' | 'assigned' => {
    const normalized = (status ?? '').toLowerCase();
    if (normalized.includes('assign')) return 'assigned';
    if (
      normalized.includes('progress') ||
      normalized.includes('pending') ||
      normalized.includes('follow')
    ) {
      return 'in-progress';
    }
    return 'open';
  };

  const conversations = useMemo(() => {
    const list = conversationsApi?.data?.data ?? [];
    return list.map((item) => ({
      ...item,
      tags: item.metadata?.tags ?? [],
      date: formatDate(item.lastMessageAt || item.createdAt),
      status: normalizeConversationStatus(item.status),
    }));
  }, [conversationsApi?.data?.data]);

  const getCustomerRoute = useCallback(
    (route: string) => {
      if (!customerId) return route;
      return route.replace(':customerId', customerId);
    },
    [customerId]
  );
  const withCustomerQuery = useCallback(
    (route: string) => {
      if (!customerId) return route;
      return `${route}?customerId=${customerId}`;
    },
    [customerId]
  );

  const modules: {
    moduleName: string;
    icon: string;
    extraIcon?: JSX.Element;
    onClick: () => void;
    disabled?: boolean;
  }[] = useMemo(
    () => [
      {
        moduleName: 'Conversations',
        icon: SVGS.conversationModuleIcon,
        onClick: () =>
          navigate(withCustomerQuery(SELLER_WEBAPP_ROUTES.CONVERSATION_LIST)),
      },
      {
        moduleName: 'Products',
        icon: SVGS.ProductsIcon,
        onClick: () =>
          navigate(getCustomerRoute(SELLER_WEBAPP_ROUTES.PRODUCTS_LIST)),
        disabled: false,
      },
      {
        moduleName: 'Orders',
        icon: SVGS.OrdersIcon_,
        onClick: () =>
          navigate(withCustomerQuery(SELLER_WEBAPP_ROUTES.ORDERS_LIST)),
      },
      {
        moduleName: 'Invoices',
        icon: SVGS.InvoicesIcon,
        onClick: () =>
          navigate(withCustomerQuery(SELLER_WEBAPP_ROUTES.INVOICES_LIST)),
      },
      {
        moduleName: 'Payments',
        icon: SVGS.PaymentsIcon,
        onClick: () =>
          navigate(withCustomerQuery(SELLER_WEBAPP_ROUTES.ACCEPT_PAYMENTS)),
      },
      {
        moduleName: 'Ledger',
        icon: SVGS.LedgerIcon,
        onClick: () =>
          navigate(getCustomerRoute(SELLER_WEBAPP_ROUTES.LEDGER_LIST)),
      },
    ],
    [navigate, getCustomerRoute, withCustomerQuery]
  );

  const refreshProfile = useCallback(() => {
    if (!customerId || !sellerWorkspaceId) return;
    getCustomerDetailsApi({
      sellerWorkspaceId,
      inviteId: customerId,
      includeInactive: true,
    });
    getCustomerProfileSummaryApi({
      sellerWorkspaceId,
      customerId,
      params: {
        inviteIds: [customerId],
      },
    });
    getConversationsApi({
      sellerWorkspaceId,
      limit: 1,
      customerId,
    });
  }, [
    customerId,
    sellerWorkspaceId,
    getCustomerDetailsApi,
    getCustomerProfileSummaryApi,
    getConversationsApi,
  ]);

  const onConversationClick = (conversations: ConversationData) => {
    console.log(conversations);
    if (!conversations?.conversationThreadId) return;

    dispatch(
      updateQueryThread({
        startMessageTimetoken: conversations?.startMessageTimetoken ?? '',
        title: conversations?.title ?? '',
        conversationId: conversations?.conversationThreadId ?? '',
        endMessageTimetoken: conversations?.endMessageTimetoken ?? '',
        customerIds: [customerId ?? ''],
        channelId: conversations?.channelId ?? '',
        channelName: conversations?.channelName ?? '',
        queryType: conversations?.queryType ?? '',
        workspaceId: conversations?.workspaceId,
        backgroundColor: '#7f2cf4',
      })
    );

    if (isFieldOps) {
      navigate(
        `${FIELD_OPS_ROUTES.ISSUE_DETAILS}/${conversations?.conversationThreadId}`?.replace(
          ':customerId',
          customerId ?? ''
        )
      );
    } else {
      navigate(
        `${SELLER_WEBAPP_ROUTES.CONVERSATION_MESSAGES.replace(':conversationId', conversations?.conversationThreadId)}?customerId=${customerId}`
      );
    }
  };

  return {
    customerId,
    customerData,
    include,
    setInclude,
    customerInfo,
    formattedMobile,
    modules,
    customerCompany: customerData?.companyName ?? '-',
    customerName: customerData?.name ?? '',
    customerIsActive: customerData?.invite?.isActive ? 1 : 0,
    displayWhatsApp:
      customerData?.excludedAndBlockedBotAudience?.isArchived === true,
    creditLimit: customerProfileSummary?.creditLimit ?? '0',
    outstandingAmount: customerProfileSummary?.outstandingAmount ?? '0',
    activities,
    conversations,
    isLoading:
      customerDetailsApi?.isUninitialized ||
      customerDetailsApi?.isLoading ||
      customerProfileSummaryApi?.isLoading ||
      conversationsApi?.isFetching,
    refreshProfile,
    onConversationClick,
  };
};
